/**
 * The batch bar and its preview dialog (#1039).
 *
 * ── This is a CALLER, not a bulk-write path ──────────────────────────────
 *
 * Applying loops `POST /api/scheduling/test-due-visits` once per site — the
 * engine that already bundles due assets, stamps `due_by_date`, links
 * `job_assets`, refreshes points and writes an audit event. A bulk endpoint
 * would have to reimplement all of that and would drift from it.
 *
 * Looping client-side buys two things that matter more than the round trips:
 * each site succeeds or fails independently, so a partial failure is visible
 * per row rather than as one toast; and progress is real rather than a
 * spinner over a black box.
 *
 * Nothing is written until the operator confirms the preview — the same safety
 * model as reconcile-from-list.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, CalendarPlus, CheckCircle2, Loader2, X } from "lucide-react";
import {
    MAX_BATCH_SITES,
    batchConfirmSentence,
    batchResultSentence,
    batchSucceededCleanly,
    summarizeBatch,
    summarizeOutcomes,
    validateSelection,
    type BatchPreviewRow,
    type RowOutcome,
} from "./batch-actions";

export function BatchBar({
    selected,
    scopeLabel,
    onClear,
    onDone,
}: {
    selected: string[];
    /** What the selection actually covers, in words. Never implied. */
    scopeLabel: string;
    onClear: () => void;
    onDone: () => void;
}) {
    const [open, setOpen] = useState(false);
    if (selected.length === 0) return null;

    const err = validateSelection(selected);

    return (
        <>
            <Card className="sticky bottom-4 z-20 border-primary/40 shadow-lg">
                <CardContent className="flex flex-wrap items-center gap-3 p-3">
                    <span className="text-sm font-semibold">
                        {selected.length} location{selected.length === 1 ? "" : "s"} selected
                    </span>
                    <span className="text-xs text-foreground/70">{scopeLabel}</span>
                    <div className="ml-auto flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClear}>
                            <X className="mr-1 h-3.5 w-3.5" /> Clear
                        </Button>
                        <Button size="sm" disabled={!!err} onClick={() => setOpen(true)}>
                            <CalendarPlus className="mr-1 h-4 w-4" /> Create jobs…
                        </Button>
                    </div>
                    {err && (
                        // Refuses, and says what to do instead. Truncating to
                        // the first 50 would report success over work that
                        // never happened.
                        <p className="w-full text-xs font-medium text-destructive">{err.message}</p>
                    )}
                </CardContent>
            </Card>
            {open && (
                <BatchPreviewDialog
                    siteIds={selected}
                    onClose={() => setOpen(false)}
                    onFinished={() => {
                        setOpen(false);
                        onClear();
                        onDone();
                    }}
                />
            )}
        </>
    );
}

function BatchPreviewDialog({
    siteIds,
    onClose,
    onFinished,
}: {
    siteIds: string[];
    onClose: () => void;
    onFinished: () => void;
}) {
    const [rows, setRows] = useState<BatchPreviewRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [outcomes, setOutcomes] = useState<Map<string, RowOutcome>>(new Map());
    const [applying, setApplying] = useState(false);
    const [done, setDone] = useState(false);

    if (rows === null && !loadError) {
        void api
            .post<{ rows: BatchPreviewRow[] }>("/api/scheduling/test-due-visits/preview", { siteIds })
            .then((r) => setRows(r.rows))
            .catch((e) =>
                setLoadError(
                    e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e),
                ),
            );
    }

    const summary = rows ? summarizeBatch(rows) : null;
    const resultSummary = summarizeOutcomes(outcomes);

    async function apply() {
        if (!rows) return;
        setApplying(true);
        const next = new Map<string, RowOutcome>();
        for (const r of rows) {
            if (r.skip_reason) next.set(r.site_id, { status: "skipped", reason: r.skip_reason });
        }
        setOutcomes(new Map(next));

        // Sequential, not Promise.all: forty concurrent job creations against
        // the same account is a thundering herd, and the per-row progress is
        // the point of doing it this way at all.
        for (const r of rows.filter((x) => x.skip_reason === null)) {
            next.set(r.site_id, { status: "creating" });
            setOutcomes(new Map(next));
            try {
                const res = await api.post<{ jobId: string; assetCount: number }>("/api/scheduling/test-due-visits", {
                    siteId: r.site_id,
                });
                next.set(r.site_id, { status: "created", jobId: res.jobId, assetCount: res.assetCount });
            } catch (e) {
                const msg =
                    e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e);
                // One site failing must never stop the rest — and must never
                // be reported as success.
                next.set(r.site_id, { status: "failed", message: msg });
            }
            setOutcomes(new Map(next));
        }
        setApplying(false);
        setDone(true);

        const s = summarizeOutcomes(next);
        if (batchSucceededCleanly(s)) toast.success(batchResultSentence(s));
        else toast.error(batchResultSentence(s));
    }

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-card shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Create jobs at {siteIds.length} locations</h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-3 p-4">
                    {loadError && <p className="text-sm text-destructive">{loadError}</p>}
                    {!rows && !loadError && <p className="text-sm text-foreground/70">Checking what would happen…</p>}

                    {summary && !done && <p className="text-sm font-medium">{batchConfirmSentence(summary)}</p>}
                    {done && <p className="text-sm font-medium">{batchResultSentence(resultSummary)}</p>}

                    {rows && (
                        <ul className="divide-y divide-border rounded-md border border-border">
                            {rows.map((r) => {
                                const o = outcomes.get(r.site_id);
                                return (
                                    <li key={r.site_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                                        <span className="min-w-0 flex-1 truncate">{r.site_name ?? "Location"}</span>
                                        {!o && r.skip_reason && (
                                            <span className="shrink-0 text-xs text-foreground/70">{r.skip_reason}</span>
                                        )}
                                        {!o && !r.skip_reason && (
                                            <span className="shrink-0 text-xs tabular-nums text-foreground/80">
                                                {r.device_count} device{r.device_count === 1 ? "" : "s"}
                                            </span>
                                        )}
                                        {o?.status === "creating" && (
                                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                                        )}
                                        {o?.status === "created" && (
                                            <Link
                                                to={`/jobs/${o.jobId}`}
                                                className="inline-flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
                                            >
                                                <CheckCircle2 className="h-3.5 w-3.5" /> {o.assetCount} device
                                                {o.assetCount === 1 ? "" : "s"}
                                            </Link>
                                        )}
                                        {o?.status === "skipped" && (
                                            <span className="shrink-0 text-xs text-foreground/70">{o.reason}</span>
                                        )}
                                        {o?.status === "failed" && (
                                            <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-destructive">
                                                <AlertTriangle className="h-3.5 w-3.5" /> {o.message}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <p className="text-xs text-foreground/60">
                        Each job is created through the same path as the New Job button — one at a time, so a failure at
                        one location never stops the rest. One batch is capped at {MAX_BATCH_SITES} locations.
                    </p>
                </div>

                <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
                    {!done ? (
                        <>
                            <Button variant="outline" onClick={onClose} disabled={applying}>
                                Cancel
                            </Button>
                            <Button onClick={apply} disabled={applying || !summary || summary.willCreate === 0}>
                                {applying ? "Creating…" : `Create ${summary?.willCreate ?? 0} jobs`}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={onFinished}>Done</Button>
                    )}
                </div>
            </div>
        </div>
    );
}
